/** What every agent screen shows when the local read failed. */

import { Button, ErrorState, Stack } from "@/components/ui";
import { agentDisplayAddress } from "@/lib/env";

import {
  AgentError,
  isUnreachable,
  needsLocalNetworkPermission,
} from "./client";

export interface AgentProblemProps {
  readonly error: unknown;
  readonly onRetry: () => void;
}

export function AgentProblem({ error, onRetry }: AgentProblemProps) {
  if (needsLocalNetworkPermission(error)) {
    return (
      <ErrorState
        headline="Let this page reach your machine"
        body={
          <Stack gap={3}>
            <p>
              The desktop app runs on this computer and this page reads it
              directly. Your browser asks permission the first time, and until
              you answer it this page cannot see the app.
            </p>
            <p>
              Press Connect, then choose Allow. If you already chose Block, open
              the padlock beside the address bar and allow local network access
              for this site.
            </p>
            <div>
              <Button variant="primary" onClick={onRetry}>
                Connect to this machine
              </Button>
            </div>
          </Stack>
        }
        code={agentDisplayAddress}
      />
    );
  }
  if (isUnreachable(error)) {
    return (
      <ErrorState
        headline="Agent not answering"
        code={agentDisplayAddress}
        onRetry={onRetry}
      />
    );
  }
  if (error instanceof AgentError) {
    return (
      <ErrorState
        headline="Refused"
        body={error.message}
        code={error.code}
        onRetry={onRetry}
      />
    );
  }
  return (
    <ErrorState
      headline="Agent not answering"
      code={agentDisplayAddress}
      onRetry={onRetry}
    />
  );
}
